'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useRef, useCallback } from 'react'
import { useTranslations } from 'next-intl'
import gsap from 'gsap'

interface Props {
  isDark:    boolean
  isScrolled: boolean
  logoUrl?:  string
}

const FALLBACK_LOGO = '/LOGOS%20PNG/LOGOS%20BIOSOIL-54.png'

export default function NavLogo({ isDark, isScrolled, logoUrl }: Props) {
  const imgRef = useRef<HTMLDivElement>(null)
  const size = isScrolled ? 84 : 104
  const t = useTranslations('nav')

  const handleEnter = useCallback(() => {
    if (!imgRef.current) return
    gsap.to(imgRef.current, {
      rotate: 4, scale: 1.05,
      duration: 0.3, ease: 'power2.out',
    })
  }, [])

  const handleLeave = useCallback(() => {
    if (!imgRef.current) return
    gsap.to(imgRef.current, {
      rotate: -4, scale: 1.05,
      duration: 0.15, ease: 'power2.inOut',
      onComplete: () => {
        gsap.to(imgRef.current, { rotate: 0, scale: 1, duration: 0.25, ease: 'power2.out' })
      },
    })
  }, [])

  return (
    <Link
      href="/"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      aria-label={t('logoAria')}
      style={{
        display: 'flex',
        alignItems: 'center',
        textDecoration: 'none',
      }}
    >
      <div
        ref={imgRef}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size,
          height: size,
          transition: 'width 300ms ease, height 300ms ease, filter 300ms ease',
          filter: isDark ? 'brightness(0) invert(1)' : 'none',
        }}
      >
        <Image
          src={logoUrl ?? FALLBACK_LOGO}
          alt="BioSoil"
          width={104}
          height={104}
          priority
          sizes="104px"
          style={{
            width: '100%',
            height: 'auto',
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </div>
    </Link>
  )
}
